<template>
  <div>
    <v-form @submit.prevent="submitResponse">
      <p class="mb-3">
        Enter appropriate identifying information for each chemical used during Fundamental Skills.
      </p>

      <div class="table-container">
        <table class="hazards-table">
          <thead>
            <tr>
              <th v-for="head in headings" :key="head.text" :style="head.style">
                {{ head.text }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="sticky-column">Crystal Violet Solution</td>
              <td style="min-width: 163px">
                <chemical-notation-input
                  v-model="inputs.chemFormulaCV"
                  :show-note="false"
                  input-placeholder="Enter formula here"
                  :disabled="!allowEditing"
                />
              </td>
              <td style="min-width: 93px" class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.mmCV"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td style="min-width: 93px">
                <v-select v-model="inputs.physStateCV" :items="stateOptions" outlined hide-details>
                  <template #item="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                  <template #selection="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                </v-select>
              </td>
              <td style="min-width: 93px" class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.vCV"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td style="min-width: 113px" class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.cCV"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td style="min-width: 193px">N/A</td>
            </tr>

            <tr>
              <td class="sticky-column">4.5% Sodium Hypochlorite Solution</td>
              <td>
                <chemical-notation-input
                  v-model="inputs.chemFormulaBleach"
                  :show-note="false"
                  input-placeholder="Enter formula here"
                  :disabled="!allowEditing"
                />
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.mmBleach"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td>
                <v-select
                  v-model="inputs.physStateBleach"
                  :items="stateOptions"
                  outlined
                  hide-details
                >
                  <template #item="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                  <template #selection="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                </v-select>
              </td>
              <td class="py-2 px-2 mx-0">N/A</td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.cBleach"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td>N/A</td>
            </tr>

            <tr>
              <td class="sticky-column">Sodium Hydroxide Stock Solution</td>
              <td>
                <chemical-notation-input
                  v-model="inputs.chemFormulaStock"
                  :show-note="false"
                  input-placeholder="Enter formula here"
                  :disabled="!allowEditing"
                />
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.mmStock"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td>
                <v-select
                  v-model="inputs.physStateStock"
                  :items="stateOptions"
                  outlined
                  hide-details
                >
                  <template #item="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                  <template #selection="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                </v-select>
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.vStock"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.cStock"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td>N/A</td>
            </tr>

            <tr>
              <td class="sticky-column">Sodium Hydroxide Solution #1 (most concentrated)</td>
              <td>
                <chemical-notation-input
                  v-model="inputs.chemFormulaSol1"
                  :show-note="false"
                  input-placeholder="Enter formula here"
                  :disabled="!allowEditing"
                />
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.mmSol1"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td>
                <v-select
                  v-model="inputs.physStateSol1"
                  :items="stateOptions"
                  outlined
                  hide-details
                >
                  <template #item="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                  <template #selection="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                </v-select>
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.vSol1"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.cSol1"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td>
                <v-select v-model="inputs.sourceSol1" :items="sourceOptions" outlined hide-details>
                  <template #item="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                  <template #selection="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                </v-select>
              </td>
            </tr>

            <tr>
              <td class="sticky-column">Sodium Hydroxide Solution #2</td>
              <td>
                <chemical-notation-input
                  v-model="inputs.chemFormulaSol2"
                  :show-note="false"
                  input-placeholder="Enter formula here"
                  :disabled="!allowEditing"
                />
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.mmSol2"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td>
                <v-select
                  v-model="inputs.physStateSol2"
                  :items="stateOptions"
                  outlined
                  hide-details
                >
                  <template #item="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                  <template #selection="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                </v-select>
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.vSol2"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.cSol2"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td>
                <v-select v-model="inputs.sourceSol2" :items="sourceOptions" outlined hide-details>
                  <template #item="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                  <template #selection="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                </v-select>
              </td>
            </tr>

            <tr>
              <td class="sticky-column">Sodium Hydroxide Solution #3 (most dilute)</td>
              <td>
                <chemical-notation-input
                  v-model="inputs.chemFormulaSol3"
                  :show-note="false"
                  input-placeholder="Enter formula here"
                  :disabled="!allowEditing"
                />
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.mmSol3"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td>
                <v-select
                  v-model="inputs.physStateSol3"
                  :items="stateOptions"
                  outlined
                  hide-details
                >
                  <template #item="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                  <template #selection="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                </v-select>
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.vSol3"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.cSol3"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td>
                <v-select v-model="inputs.sourceSol3" :items="sourceOptions" outlined hide-details>
                  <template #item="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                  <template #selection="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                </v-select>
              </td>
            </tr>

            <tr>
              <td class="sticky-column">Deionized Water</td>
              <td>
                <chemical-notation-input
                  v-model="inputs.chemFormulaH2O"
                  :show-note="false"
                  input-placeholder="Enter formula here"
                  :disabled="!allowEditing"
                />
              </td>
              <td class="py-2 px-2 mx-0 centered-input">
                <calculation-input
                  v-model="inputs.mmH2O"
                  class="centered-input"
                  :disabled="!allowEditing"
                />
              </td>
              <td>
                <v-select v-model="inputs.physStateH2O" :items="stateOptions" outlined hide-details>
                  <template #item="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                  <template #selection="{item}">
                    <stemble-latex class="no-text-transform" :content="item" />
                  </template>
                </v-select>
              </td>
              <td>N/A</td>
              <td>N/A</td>
              <td>N/A</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p class="mt-5">Select all the glassware/equipment/instrumentation used in this lab.</p>
      <v-select v-model="inputs.equipment" :items="equipmentOptions" multiple dense outlined chips>
      </v-select>
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import StembleLatex from '@/tasks/components/StembleLatex.vue';
import ChemicalLatex from '@/tasks/components/displayers/ChemicalLatex.vue';
import ChemicalNotationInput from '@/chemical-equations/components/ChemicalNotationInput.vue';
import CalculationInput from '@/tasks/components/inputs/CalculationInput.vue';

export default {
  name: 'ChemUCI1LCFS3IL2',
  components: {
    CalculationInput,
    ChemicalNotationInput,
    ChemicalLatex,
    StembleLatex,
  },
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      inputs: {
        chemFormulaCV: null,
        mmCV: null,
        physStateCV: null,
        vCV: null,
        cCV: null,

        chemFormulaBleach: null,
        mmBleach: null,
        physStateBleach: null,
        vBleach: null,
        cBleach: null,

        chemFormulaStock: null,
        mmStock: null,
        physStateStock: null,
        vStock: null,
        cStock: null,

        chemFormulaSol1: null,
        mmSol1: null,
        physStateSol1: null,
        vSol1: null,
        cSol1: null,
        sourceSol1: null,

        chemFormulaSol2: null,
        mmSol2: null,
        physStateSol2: null,
        vSol2: null,
        cSol2: null,
        sourceSol2: null,

        chemFormulaSol3: null,
        mmSol3: null,
        physStateSol3: null,
        vSol3: null,
        cSol3: null,
        sourceSol3: null,

        chemFormulaH2O: null,
        mmH2O: null,
        physStateH2O: null,

        equipment: [],
      },
      headings: [
        {text: 'Chemical Name', style: 'text-align: left'},
        {text: 'Formula', style: 'text-align: center'},
        {text: 'Molar Mass (g/mol)', style: 'text-align: center'},
        {text: 'Physical States', style: 'text-align: center'},
        {text: 'Total Volume Used (mL)', style: 'text-align: center'},
        {text: 'Concentration (M)', style: 'text-align: center'},
        {text: '1/10 Dilution: Source of Vconc', style: 'text-align: center'},
      ],
      stateOptions: ['l', 's', 'g', 'aq'],
      sourceOptions: [
        'Crystal Violet Solution',
        '4.5% Sodium Hypochlorite Solution',
        'Sodium Hydroxide Stock Solution',
        'Sodium Hydroxide Solution #1 (most concentrated)',
        'Sodium Hydroxide Solution #2',
        'Sodium Hydroxide Solution #3 (most dilute)',
        'Deionized Water',
      ],
      equipmentOptions: [
        'beaker(s)',
        'Buchner funnel',
        'buret',
        'capillary/melting tube',
        'clamp and stand',
        'cuvette',
        'Digi-Melt (melting point apparatus)',
        'digital balance',
        'disposable pipet',
        'drying oven',
        'Erlenmeyer flask',
        'filter paper',
        'funnel',
        'graduated cylinder',
        'ice',
        'KimWipes (tissues)',
        'LabQuest',
        'pencil',
        'pipet suction bulb',
        'probe (pH)',
        'probe (temperature)',
        'scintillation vial',
        'side arm flask',
        'spatula or scoopula',
        'spectrometer',
        'spotter',
        'stir bar',
        'stirrer/hotplate',
        'test tube',
        'thermometer (alcohol)',
        'TLC (developing) chamber',
        'TLC plate',
        'TLC spotter',
        'UV lamp',
        'volumetric flask',
        'volumetric pipet',
        'watch glass',
        'weighing boat or paper',
      ],
    };
  },
};
</script>

<style scoped>
.table-container {
  position: relative;
  overflow-x: auto;
  background: /* Shadow Cover RIGHT */
    linear-gradient(to left, white 1rem, rgba(255, 255, 255, 0)) center right,
    /* Shadow RIGHT */ linear-gradient(to left, rgba(27, 19, 53, 0.07), rgba(27, 19, 53, 0)) center
      right,
    /* Shadow Cover BOTTOM */ linear-gradient(to top, white 1rem, rgba(255, 255, 255, 0)) bottom
      center,
    /* Shadow BOTTOM */ linear-gradient(to top, rgba(27, 19, 53, 0.07), rgba(27, 19, 53, 0)) bottom
      center,
    white;
  background-repeat: no-repeat;
  background-size:
    3rem 100%,
    1rem 100%,
    100% 3rem,
    100% 1rem,
    100% 100%;
  background-attachment: local, scroll, local, scroll, local;
  border-radius: 0.5rem;
  border: 1px solid #e0e0e0;
  box-shadow:
    0px 1px 2px 0px rgba(18, 11, 40, 0.05),
    1px 4px 14px 0px rgba(44, 32, 80, 0.06);
}

.hazards-table {
  width: 100%;
  border-collapse: collapse;
  position: relative;
}

.hazards-table th,
.hazards-table td {
  padding: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  //white-space: nowrap;
}

.hazards-table th {
  position: sticky;
  top: 0;
  background: white;
  z-index: 2;
}

.sticky-column {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}

.sticky-column::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 1px;
  background-color: #e0e0e0;
  z-index: 2;
}

.sticky-column:first-child {
  left: 0;
}

/* Ensure sticky header cells are above sticky columns */
.hazards-table th.sticky-column {
  z-index: 3;
}

.centered-input {
  text-align: center;
}
</style>
